import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t } from '../i18n.js';
import { ERNST } from '../components/Status.jsx';
import { MailRegels } from './BeheerMeldingen.jsx';

/**
 * De meldingsregels van één klant (Beheer › Klant). Volledig per klant: elke klant een eigen Webex-ruimte,
 * mailadressen, ernst en tijden. De transportlaag (mailserver, Webex-bot) en de kantooruren zijn algemeen en
 * staan onder Beheer › Meldingen. Zie docs/besluiten/meldingen-per-klant-instellen.md.
 */
export default function KlantMeldingen({ code }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['klant-meldingen', code], queryFn: () => api.notificatiesKlant(code) });
  const alg = useQuery({ queryKey: ['beheer-meldingen'], queryFn: api.beheerMeldingen });
  const [m, zetM] = useState(null);
  const [melding, zetMelding] = useState(null);
  useEffect(() => { zetM(null); zetMelding(null); }, [code]);
  useEffect(() => { if (q.data && m === null) zetM(q.data.data.notifications); }, [q.data, m]);
  if (q.isPending || m === null) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const uren = alg.data?.data.officeHours ?? { van: '08:00', tot: '17:30' };
  const { modules } = q.data.data;

  const zet = (pad, waarde) => zetM((x) => {
    const y = structuredClone(x);
    let o = y; for (const k of pad.slice(0, -1)) o = o[k];
    o[pad.at(-1)] = waarde;
    return y;
  });
  const wissel = (lijst, w, aan) => (aan ? [...new Set([...lijst, w])] : lijst.filter((x) => x !== w));
  const bewaar = async () => {
    zetMelding(null);
    try {
      await api.zetNotificatiesKlant(code, m);
      zetMelding({ goed: true, tekst: t('Opgeslagen.') });
      await client.invalidateQueries({ queryKey: ['klant-meldingen', code] });
    } catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
  };
  const test = async () => {
    zetMelding(null);
    try {
      const { data } = await api.testMelding(code);
      zetMelding({ goed: true, tekst: t('Testbericht naar {n} bestemming(en). De uitkomst staat onder Beheer › Meldingen.', { n: data.queued }) });
    } catch (e) { zetMelding({ goed: false, tekst: t(e.message) }); }
  };
  const ernstVinkjes = () => (
    <div className="themarij" style={{ gap: 12 }}>
      {Object.entries(ERNST).map(([k, l]) => (
        <label key={k} className="vink"><input type="checkbox" checked={m.webex.ernst.includes(k)}
          onChange={(e) => zet(['webex', 'ernst'], wissel(m.webex.ernst, k, e.target.checked))} />{t(l)}</label>
      ))}
    </div>
  );

  return (
    <>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 14 }}>{melding.tekst}</div>}
      <section className="card setblok">
        <span className="label">{t('Meldingen van deze klant')}</span>
        <p className="noot" style={{ marginTop: 0 }}>{t('Deze meldingen gelden bovenop de algemene meldingen naar ADTS (Beheer › Meldingen). Laat je ze leeg, dan krijgt deze klant alleen de algemene.')}</p>
        <div className="setrij">
          <div className="setkop"><b>{t('Webex buiten kantooruren')}</b>
            <span>{t('Meteen, dag en nacht (24/7), of vasthouden tot de werkdag begint. Mail gaat per regel, elk met zijn eigen tijd (hieronder). Een geslaagde VPN-login na een reeks mislukte gaat altijd meteen naar Webex.')}</span></div>
          <div className="themarij">
            {[['webex', 'Webex meteen, 24/7'], ['vasthouden', 'Webex vasthouden tot de werkdag begint']].map(([k, l]) => (
              <label key={k} className="vink"><input type="radio" name="buiten" checked={m.buitenKantooruren === k} onChange={() => zet(['buitenKantooruren'], k)} />{t(l)}</label>
            ))}
          </div>
        </div>
        <div className="setrij">
          <div className="setkop"><b>Webex</b><span>{t('De ruimte (room ID) van het SOC van deze klant, en welke ernst er naartoe gaat.')}</span></div>
          <div className="form" style={{ gap: 10 }}>
            <label className="vink"><input type="checkbox" checked={m.webex.aan} onChange={(e) => zet(['webex', 'aan'], e.target.checked)} />{t('Aan')}</label>
            <label className="field"><span>{t('Ruimte (room ID)')}</span>
              <input className="input mono" value={m.webex.ruimte} onChange={(e) => zet(['webex', 'ruimte'], e.target.value)} placeholder="Y2lzY29zcGFyazovL3VzL1JPT00v…" /></label>
            {ernstVinkjes()}
          </div>
        </div>
        <div className="setrij">
          <div className="setkop"><b>{t('Mail')}</b><span>{t('Mailregels: per regel wie, welke alerts en wanneer.')}</span></div>
          <div className="form" style={{ gap: 10 }}>
            <label className="vink"><input type="checkbox" checked={m.mail.aan} onChange={(e) => zet(['mail', 'aan'], e.target.checked)} />{t('Aan')}</label>
            <MailRegels regels={m.mail.regels} modules={modules} uren={uren} zet={(r) => zet(['mail', 'regels'], r)} />
          </div>
        </div>
        <div className="actions">
          <button className="btn primary" onClick={bewaar}>{t('Opslaan')}</button>
          <button className="btn" onClick={test}>{t('Stuur een testbericht')}</button>
        </div>
      </section>
    </>
  );
}
